import React, { useState, useEffect, useRef } from 'react';
import DefaultEditor from "react-simple-wysiwyg";
import { latex_syntax, SUPERSCRIPT_MAPPING } from "../../data/json/latexSyntax";

interface CommonTextEditorProps {
    id?: string;
    defaultValue?: string; // Optional prop for the default value
    onChange?: (event: any) => void;
    showSyntaxHelper?: boolean; // Option to show/hide syntax helper feature
}

interface ToolboxDialogData {
  html: string;
  description: string;
  variables: string[];
  fixTag?: string;
}

const CommonTextEditor: React.FC<CommonTextEditorProps> = ({ 
    id, 
    defaultValue, 
    onChange,
    showSyntaxHelper = true // Default to true to show syntax helper
}) => {
    const [value, setValue] = useState(defaultValue || ""); // Use defaultValue as the initial value
    const [showSyntax, setShowSyntax] = useState(false);
    const [activeTab, setActiveTab] = useState<string>("Operators");
    const [showToolboxDialog, setShowToolboxDialog] = useState(false);
    const [toolboxData, setToolboxData] = useState<ToolboxDialogData | null>(null);
    const [variableValues, setVariableValues] = useState<Record<string, string>>({});
    const editorRef = useRef<HTMLDivElement>(null);

    const tabs = [
        "Operators",
        "Relations",
        "Arrows",
        "Lowercase Greek Letters",
        "Uppercase Greek Letters"
    ];

    // Update local value when defaultValue changes
    useEffect(() => {
        setValue(defaultValue || "");
    }, [defaultValue]);

    const handleChange = (e: any) => {
        setValue(e.target.value);
        console.log("rich text editor value", e.target.value);
        onChange?.(e);
    };

    // Convert text to superscript unicode characters
    const convertToSuperscript = (text: string): string => {
        return text.split('').map(char => {
            if (char in SUPERSCRIPT_MAPPING) {
                return SUPERSCRIPT_MAPPING[char as keyof typeof SUPERSCRIPT_MAPPING];
            }
            return char;
        }).join('');
    };

    // Extract variables from HTML string
    const extractVariables = (html: string): string[] => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const text = tempDiv.textContent || '';
        const matches = text.match(/[a-z]/g) || [];
        const uniqueVars = Array.from(new Set(matches)).sort();
        return uniqueVars;
    };

    // Handle variable replacement for sup tags with superscript unicode conversion
    const handleSupTagReplacement = (html: string, values: Record<string, string>): string => {
        let result = html;
        
        Object.keys(values).forEach(variable => {
            if (values[variable]) {
                const supRegex = new RegExp(`(<sup[^>]*>)([^<]*${variable}[^<]*)(<\\/sup>)`, 'g');
                
                result = result.replace(supRegex, (match, openTag, content, closeTag) => {
                    const regex = new RegExp(`(?<![a-zA-Z])${variable}(?![a-zA-Z])`, 'g');
                    const replacedContent = content.replace(regex, () => {
                        return convertToSuperscript(values[variable]);
                    });
                    return openTag + replacedContent + closeTag;
                });
                
                const outsideSupRegex = new RegExp(`(?<![a-zA-Z])${variable}(?![a-zA-Z])(?![^<]*<\\/sup>)`, 'g');
                result = result.replace(outsideSupRegex, (match) => {
                    const beforeMatch = result.substring(0, result.indexOf(match));
                    const lastSupOpen = beforeMatch.lastIndexOf('<sup');
                    const lastSupClose = beforeMatch.lastIndexOf('</sup>');
                    
                    if (lastSupClose >= lastSupOpen) {
                        return values[variable];
                    }
                    return match;
                });
            }
        });
        
        return result;
    };

    // Handle normal variable replacement
    const handleNormalReplacement = (html: string, values: Record<string, string>): string => {
        let result = html;
        
        Object.keys(values).forEach(variable => {
            if (values[variable]) {
                const regex = new RegExp(`(?<![a-zA-Z])${variable}(?![a-zA-Z])`, 'g');
                result = result.replace(regex, values[variable]);
            }
        });
        
        return result;
    };

    // Replace variables in HTML with provided values
    const replaceVariables = (html: string, values: Record<string, string>, fixTag?: string): string => {
        if (fixTag === 'sup') {
            return handleSupTagReplacement(html, values);
        }
        return handleNormalReplacement(html, values);
    };

    // Handle inserting symbol into this editor
    const handleInsertSymbol = (html: string) => {
        const currentText = value || '';
        const newText = currentText + (currentText ? '&nbsp;' : '') + html + "&nbsp;";
        
        setValue(newText);
        
        // Trigger onChange event
        if (onChange) {
            onChange({ target: { value: newText } });
        }
        
        // Focus the editor after update
        setTimeout(() => {
            const editorContainer = document.getElementById(id || '');
            if (editorContainer) {
                const contentEditable = editorContainer.querySelector('.rsw-ce') as HTMLDivElement;
                if (contentEditable) {
                    contentEditable.focus();
                    const range = document.createRange();
                    const selection = window.getSelection();
                    range.selectNodeContents(contentEditable);
                    range.collapse(false);
                    selection?.removeAllRanges();
                    selection?.addRange(range);
                }
            }
        }, 10);
    };

    // Handle card click
    const handleCardClick = (item: any) => {
        if (item.toolbox === "true") {
            const variables = extractVariables(item.html);
            setToolboxData({
                html: item.html,
                description: item.description,
                variables,
                fixTag: item.fixTag
            });
            
            const initialValues: Record<string, string> = {};
            variables.forEach(v => initialValues[v] = '');
            setVariableValues(initialValues);
            
            setShowToolboxDialog(true);
        } else {
            handleInsertSymbol(item.html);
        }
    };

    // Handle OK button in toolbox dialog
    const handleToolboxOk = () => {
        if (toolboxData) {
            const finalHtml = replaceVariables(toolboxData.html, variableValues, toolboxData.fixTag);
            handleInsertSymbol(finalHtml);
        }
        setShowToolboxDialog(false);
        setToolboxData(null);
        setVariableValues({});
    };

    // Handle Cancel button in toolbox dialog
    const handleToolboxCancel = () => {
        setShowToolboxDialog(false);
        setToolboxData(null);
        setVariableValues({});
    };

    return (
        <div className="w-100" ref={editorRef}>
            {showSyntaxHelper && (
                <div className="mb-2">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setShowSyntax(!showSyntax)}
                    >
                        {showSyntax ? 'Hide' : 'Show'} Scientific Syntax
                    </button>
                </div>
            )}

            {showSyntaxHelper && showSyntax && (
                <div className="border rounded bg-light mb-3">
                    {/* Tab Navigation */}
                    <ul className="nav nav-tabs nav-tabs-solid bg-white border-bottom">
                        {tabs.map((tab) => (
                            <li className="nav-item" key={tab}>
                                <button
                                    className={`nav-link ${activeTab === tab ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab)}
                                    type="button"
                                    style={{ 
                                        cursor: 'pointer',
                                        fontSize: '0.875rem',
                                        padding: '0.5rem 1rem'
                                    }}
                                >
                                    {tab}
                                </button>
                            </li>
                        ))}
                    </ul>

                    {/* Tab Content - Horizontal Scrollable Cards */}
                    <div
                        className="p-2"
                        style={{
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        <div className="d-flex gap-2" style={{ paddingBottom: '0.25rem' }}>
                            {latex_syntax[activeTab as keyof typeof latex_syntax]?.map((item, index) => (
                                <div
                                    key={index}
                                    className="card border"
                                    style={{
                                        minWidth: '90px',
                                        maxWidth: '90px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        display: 'inline-block',
                                        whiteSpace: 'normal'
                                    }}
                                    onClick={() => handleCardClick(item)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}
                                    title="Click to insert"
                                >
                                    <div className="card-body p-2 text-center">
                                        <div className="mb-1">
                                            <small className="text-muted d-block" style={{ fontSize: '0.625rem', fontWeight: 500, lineHeight: '1.2' }}>
                                                {item.description}
                                            </small>
                                        </div>
                                        
                                        <div className="mb-1">
                                            <code 
                                                className="text-primary d-block"
                                                style={{ 
                                                    fontSize: '0.65rem',
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '0.125rem 0.25rem',
                                                    borderRadius: '0.125rem',
                                                    fontWeight: 600,
                                                    wordBreak: 'break-all',
                                                    lineHeight: '1.3'
                                                }}
                                            >
                                                {item.html}
                                            </code>
                                        </div>
                                        
                                        <div 
                                            style={{ 
                                                fontSize: '1.2rem', 
                                                minHeight: '25px', 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center',
                                                fontWeight: 'bold'
                                            }}
                                            dangerouslySetInnerHTML={{ __html: item.html }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <DefaultEditor
                id={id}
                containerProps={{
                    className: "rsw-editor w-100"
                }}
                value={value} 
                onChange={handleChange}
            />

            {/* Toolbox Variable Input Dialog */}
            {showToolboxDialog && toolboxData && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Enter Values for {toolboxData.description}</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={handleToolboxCancel}
                                    aria-label="Close"
                                />
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label text-muted small">Preview Template:</label>
                                    <div 
                                        className="p-2 bg-light border rounded text-center"
                                        style={{ fontSize: '1.5rem' }}
                                        dangerouslySetInnerHTML={{ __html: toolboxData.html }}
                                    />
                                </div>
                                
                                {toolboxData.variables.map((variable) => (
                                    <div key={variable} className="mb-3">
                                        <label className="form-label">
                                            Enter value for <strong>{variable}</strong>:
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={variableValues[variable] || ''}
                                            onChange={(e) => {
                                                setVariableValues({
                                                    ...variableValues,
                                                    [variable]: e.target.value
                                                });
                                            }}
                                            placeholder={`Value for ${variable}`}
                                            autoFocus={variable === toolboxData.variables[0]}
                                        />
                                    </div>
                                ))}

                                {Object.keys(variableValues).length > 0 && Object.values(variableValues).some(v => v) && (
                                    <div className="mt-3">
                                        <label className="form-label text-muted small">Preview with your values:</label>
                                        <div 
                                            className="p-2 bg-success bg-opacity-10 border text-dark border-success rounded text-center"
                                            style={{ fontSize: '1.5rem' }}
                                            dangerouslySetInnerHTML={{ 
                                                __html: replaceVariables(toolboxData.html, variableValues, toolboxData.fixTag) 
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-light" 
                                    onClick={handleToolboxCancel}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary" 
                                    onClick={handleToolboxOk}
                                    disabled={!Object.values(variableValues).some(v => v)}
                                >
                                    Insert
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CommonTextEditor;
